import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Cell } from '@/shared/board/types'

/**
 * Камера на телефоне в портрете.
 *
 * Почти всё время видна вся доска: игрок должен видеть обстановку — чья
 * фишка за чьей, кто кого срубит следующим ходом. Постоянное приближение это
 * отнимало, и в партиях с ним проигрывали именно из-за того, чего не было в
 * кадре. Поэтому приближение — только в момент выбора своего хода и ровно
 * такое, чтобы все варианты поместились в кадр: ни одна цель не за краем.
 *
 * Движение — одним плавным переходом сдвига и масштаба (transform), а не
 * прокруткой. Прокрутка браузера на телефонах шла рывками, а масштаб и
 * сдвиг, меняясь по отдельности, дёргали доску дважды.
 *
 * Пальцем приближённую доску можно сдвинуть; сдвиг больше порога — это
 * жест, а не нажатие, и ход им не делается.
 */

const PORTRAIT_PHONE = '(max-width: 60rem) and (orientation: portrait)'

/** Дальше доска теряет ориентиры. */
const MAX_ZOOM = 1.8
/**
 * Слабее, чем в столько раз, не наезжаем: клетка почти не крупнеет, а край
 * доски уже срезан — обстановку теряем, ничего не получив взамен.
 */
const WORTH_ZOOM = 1.4
/** Поля вокруг нужных клеток — в клетках. */
const PAD = 1.3
/** Столько пикселей пальцем — уже сдвиг доски, а не нажатие. */
const DRAG = 8

function phoneNow(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(PORTRAIT_PHONE).matches
}

/** Что видно в окне — доли доски, для рамки на мини-карте. */
export type CameraFrame = { x: number; y: number; w: number; h: number }

/** Положение слоя доски: сдвиг в пикселях и масштаб. */
type Shot = { x: number; y: number; scale: number }

export function usePhoneCamera() {
  const [phone, setPhone] = useState(phoneNow)
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  const [room, setRoom] = useState({ width: 0, height: 0 })
  const [shot, setShot] = useState<Shot>({ x: 0, y: 0, scale: 1 })
  // Сдвиг пальцем идёт мимо React: перерисовывать экран партии на каждый
  // кадр жеста — ровно то, от чего доска дёргалась.
  const live = useRef(shot)
  const seen = useRef('')
  const want = useRef<{ focus: Cell[]; size: number } | null>(null)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const watch = window.matchMedia(PORTRAIT_PHONE)
    const react = (): void => setPhone(watch.matches)

    watch.addEventListener('change', react)

    return () => watch.removeEventListener('change', react)
  }, [])

  useLayoutEffect(() => {
    if (!phone || box === null) return

    const fit = (): void => setRoom({ width: box.clientWidth, height: box.clientHeight })

    fit()

    const observer = new ResizeObserver(fit)

    observer.observe(box)

    return () => observer.disconnect()
  }, [phone, box])

  /** Кадр под нужные клетки, а без них — вся доска по центру окна. */
  const frameFor = useCallback(
    (focus: Cell[] | null, size: number): Shot => {
      const { width, height } = room
      const side = width
      const overview = { x: 0, y: Math.max(0, (height - side) / 2), scale: 1 }

      if (focus === null || focus.length === 0 || width === 0) return overview

      const cell = side / (size + 1)
      const left = (Math.min(...focus.map((one) => one.col)) + 0.5 - PAD) * cell
      const right = (Math.max(...focus.map((one) => one.col)) + 1.5 + PAD) * cell
      const top = (Math.min(...focus.map((one) => one.row)) + 0.5 - PAD) * cell
      const bottom = (Math.max(...focus.map((one) => one.row)) + 1.5 + PAD) * cell
      const scale = Math.min(MAX_ZOOM, width / (right - left), height / (bottom - top))

      if (scale < WORTH_ZOOM) return overview

      const board = side * scale
      const clamp = (value: number, span: number, view: number) =>
        span <= view ? (view - span) / 2 : Math.min(0, Math.max(view - span, value))

      return {
        x: clamp(width / 2 - ((left + right) / 2) * scale, board, width),
        y: clamp(height / 2 - ((top + bottom) / 2) * scale, board, height),
        scale,
      }
    },
    [room],
  )

  /*
   * Навести камеру — один раз на ситуацию (key). null вместо клеток — общий
   * план. Функцией, а не аргументами хука: что важно, экран партии узнаёт
   * уже при отрисовке, после ранних выходов, а хуки обязаны стоять до них.
   */
  const look = useCallback(
    (focus: Cell[] | null, key: string, size: number): void => {
      want.current = focus === null ? null : { focus, size }

      if (!phone || seen.current === key) return

      seen.current = key

      const next = frameFor(focus, size)

      live.current = next
      setShot(next)
    },
    [phone, frameFor],
  )

  // Сменился размер окна (повернули телефон, открылась клавиатура) — тот же
  // кадр под новые размеры.
  useEffect(() => {
    if (!phone || room.width === 0) return

    const next = frameFor(want.current?.focus ?? null, want.current?.size ?? 0)

    live.current = next
    setShot(next)
  }, [phone, room, frameFor])

  // Сдвиг пальцем — только приближённой доски: целиком она и так видна.
  useEffect(() => {
    if (!phone || box === null) return

    const layer = box.firstElementChild

    if (!(layer instanceof HTMLElement)) return

    let from: { x: number; y: number; at: Shot } | null = null
    let dragged = false

    const down = (event: PointerEvent): void => {
      if (live.current.scale <= 1) return

      from = { x: event.clientX, y: event.clientY, at: live.current }
      dragged = false
    }

    const move = (event: PointerEvent): void => {
      if (from === null) return

      const dx = event.clientX - from.x
      const dy = event.clientY - from.y

      if (!dragged && Math.hypot(dx, dy) < DRAG) return

      dragged = true

      const board = box.clientWidth * from.at.scale
      const clamp = (value: number, span: number, view: number) =>
        span <= view ? (view - span) / 2 : Math.min(0, Math.max(view - span, value))

      live.current = {
        x: clamp(from.at.x + dx, board, box.clientWidth),
        y: clamp(from.at.y + dy, board, box.clientHeight),
        scale: from.at.scale,
      }
      layer.style.transition = 'none'
      layer.style.transform = transformOf(live.current)
    }

    const up = (): void => {
      if (from === null) return

      from = null

      if (!dragged) return

      layer.style.transition = ''
      setShot(live.current)
    }

    // Жест, а не нажатие: клик после сдвига до целей не доходит.
    const click = (event: MouseEvent): void => {
      if (!dragged) return

      dragged = false
      event.stopPropagation()
      event.preventDefault()
    }

    box.addEventListener('pointerdown', down)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    box.addEventListener('click', click, true)

    return () => {
      box.removeEventListener('pointerdown', down)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      box.removeEventListener('click', click, true)
    }
  }, [phone, box])

  /** Вернуть общий план — кнопкой «вся доска». */
  const overview = useCallback(() => {
    const next = frameFor(null, 0)

    live.current = next
    setShot(next)
  }, [frameFor])

  const side = room.width * shot.scale
  const frame: CameraFrame =
    side === 0
      ? { x: 0, y: 0, w: 1, h: 1 }
      : {
          x: Math.max(0, -shot.x / side),
          y: Math.max(0, -shot.y / side),
          w: Math.min(1, room.width / side),
          h: Math.min(1, room.height / side),
        }

  return {
    phone,
    view: setBox,
    base: room.width,
    transform: transformOf(shot),
    zoomed: shot.scale > 1,
    scale: shot.scale,
    frame,
    look,
    overview,
  }
}

function transformOf(shot: Shot): string {
  return `translate(${shot.x}px, ${shot.y}px) scale(${shot.scale})`
}

/** Навести камеру из отрисовки: эффект, потому что доска должна быть в разметке. */
export function CameraAim({
  look,
  focus,
  focusKey,
  size,
}: {
  look: (focus: Cell[] | null, key: string, size: number) => void
  focus: Cell[] | null
  focusKey: string
  size: number
}): null {
  // biome-ignore lint/correctness/useExhaustiveDependencies: ситуацию задаёт ключ, а не массив клеток
  useEffect(() => {
    // Пустой ключ — «оставить как есть»: камера стоит там, где стоит.
    if (focusKey !== '') look(focus, focusKey, size)
  }, [look, focusKey, size])

  return null
}
